import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ShieldCheck, Clock, Award, X, ArrowRight } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
type Provider = {
  id: string;
  name: string;
  specialty: string;
  years: number;
  quote: string;
  benefit: string;
  image: string;
};
const categories = [{
  name: 'Legal & Residency',
  description: 'Navigate Mexican law and immigration with ease.',
  providers: [{
    id: 'l1',
    name: 'Lex Advisors Cabo',
    specialty: 'Real Estate Closings & Entity Setup',
    years: 15,
    quote: 'They made setting up our Mexican corporation entirely painless. Flawless execution.',
    benefit: 'Collective Price: $6,000 (vs standard $10,000) & 7-day turnkey entity setup.',
    image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80'
  }, {
    id: 'l2',
    name: 'Baja Immigration Services',
    specialty: 'Permanent Residency & Visas',
    years: 12,
    quote: 'Got our permanent residency sorted while we were still in the US. Incredible service.',
    benefit: 'Fast-track: 30 days (vs standard 6-12 months) for $5,000.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80'
  }]
}, {
  name: 'Design & Build',
  description: 'Transform your property with trusted visionaries.',
  providers: [{
    id: 'd1',
    name: 'Cabo Constructora',
    specialty: 'General Contracting & New Builds',
    years: 20,
    quote: 'On time, on budget, and the craftsmanship is world-class. No surprises.',
    benefit: 'Priority scheduling: Start within 60 days (vs standard 12-18 month waitlist).',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80'
  }, {
    id: 'd2',
    name: 'Studio Baja',
    specialty: 'Luxury Interior Design',
    years: 8,
    quote: 'They perfectly captured the indoor-outdoor Cabo aesthetic we dreamed of.',
    benefit: 'Complimentary initial 3D rendering and 15% off custom furniture sourcing.',
    image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80'
  }]
}];
export function Collective() {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this sends the lead to the backend for referral tracking
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setSelectedProvider(null);
      setLeadForm({
        name: '',
        email: '',
        phone: ''
      });
    }, 3000);
  };
  return <div className="w-full bg-sand min-h-screen pt-24" data-id="element-117">
      {/* Header */}
      <section className="py-20 px-4 bg-navy text-sand text-center" data-id="element-118">
        <div className="max-w-4xl mx-auto" data-id="element-119">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-serif text-white mb-6" data-id="element-120">
            The Collective
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-lg md:text-xl font-light text-sand/80 leading-relaxed mb-10" data-id="element-121">
            A curated directory of vetted, elite professionals. We've done the
            background checks, verified the track records, and negotiated
            exclusive benefits so you can build, buy, and live in Cabo with
            absolute confidence.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex flex-wrap justify-center gap-8 text-sm uppercase tracking-wider font-semibold text-gold" data-id="element-122">
            <div className="flex items-center" data-id="element-123">
              <ShieldCheck className="mr-2" size={18} data-id="element-124" /> Vetted
            </div>
            <div className="flex items-center" data-id="element-125">
              <Award className="mr-2" size={18} data-id="element-126" /> Proven
            </div>
            <div className="flex items-center" data-id="element-127">
              <Star className="mr-2" size={18} data-id="element-128" /> Exclusive Pricing
            </div>
          </motion.div>
        </div>
      </section>

      {/* Directory */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-id="element-129">
        {categories.map((category, idx) => <div key={idx} className="mb-24" data-id="element-130">
            <motion.div {...fadeIn} className="mb-12 border-b border-navy/10 pb-6" data-id="element-131">
              <h2 className="text-3xl font-serif text-navy mb-2" data-id="element-132">
                {category.name}
              </h2>
              <p className="text-navy/60 font-light text-lg" data-id="element-133">
                {category.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-134">
              {category.providers.map((provider, pIdx) => <motion.div key={provider.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: pIdx * 0.1
          }} className="bg-white flex flex-col h-full shadow-sm hover:shadow-md transition-shadow" data-id="element-135">
                  <div className="h-48 overflow-hidden relative" data-id="element-136">
                    <img src={provider.image} alt={provider.name} className="w-full h-full object-cover" data-id="element-137" />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold text-navy uppercase tracking-wider" data-id="element-138">
                      {provider.years} Years Exp.
                    </div>
                  </div>
                  <div className="p-8 flex-grow flex flex-col" data-id="element-139">
                    <h3 className="text-2xl font-serif text-navy mb-1" data-id="element-140">
                      {provider.name}
                    </h3>
                    <p className="text-gold text-sm font-semibold uppercase tracking-wider mb-6" data-id="element-141">
                      {provider.specialty}
                    </p>

                    <blockquote className="border-l-2 border-gold pl-4 text-navy/70 font-light italic mb-6 flex-grow" data-id="element-142">
                      "{provider.quote}"
                    </blockquote>

                    <div className="bg-sand p-4 mb-6" data-id="element-143">
                      <div className="flex items-start" data-id="element-144">
                        <Star className="text-gold mr-3 shrink-0 mt-0.5" size={18} data-id="element-145" />
                        <p className="text-sm text-navy font-medium" data-id="element-146">
                          <span className="block text-xs text-navy/60 uppercase tracking-wider mb-1" data-id="element-147">
                            Collective Benefit
                          </span>
                          {provider.benefit}
                        </p>
                      </div>
                    </div>

                    <button onClick={() => setSelectedProvider(provider)} className="w-full py-3 border border-navy text-navy uppercase tracking-wider text-sm font-semibold hover:bg-navy hover:text-white transition-colors flex items-center justify-center group" data-id="element-148">
                      Connect with {provider.name.split(' ')[0]}
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} data-id="element-149" />
                    </button>
                  </div>
                </motion.div>)}
            </div>
          </div>)}
      </section>

      {/* Lead Capture Modal */}
      <AnimatePresence data-id="element-150">
        {selectedProvider && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/80 backdrop-blur-sm" data-id="element-151">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white w-full max-w-lg relative overflow-hidden shadow-2xl" data-id="element-152">
              <button onClick={() => setSelectedProvider(null)} className="absolute top-4 right-4 text-navy/50 hover:text-navy z-10" data-id="element-153">
                <X size={24} data-id="element-154" />
              </button>

              <div className="p-8 md:p-10" data-id="element-155">
                {submitted ? <div className="text-center py-10" data-id="element-156">
                    <ShieldCheck className="mx-auto text-gold mb-4" size={48} data-id="element-157" />
                    <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-158">
                      Introduction Sent
                    </h3>
                    <p className="text-navy/70 font-light" data-id="element-159">
                      We've notified {selectedProvider.name}. They will reach
                      out to you shortly with your exclusive Collective
                      benefits.
                    </p>
                  </div> : <>
                    <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-160">
                      Connect with {selectedProvider.name}
                    </h3>
                    <p className="text-navy/60 font-light mb-8 text-sm" data-id="element-161">
                      Provide your details below to claim your exclusive
                      Collective benefits and initiate an introduction.
                    </p>

                    <form onSubmit={handleConnect} className="space-y-4" data-id="element-162">
                      <div data-id="element-163">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-164">
                          Full Name
                        </label>
                        <input type="text" required value={leadForm.name} onChange={e => setLeadForm({
                    ...leadForm,
                    name: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-165" />
                      </div>
                      <div data-id="element-166">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-167">
                          Email Address
                        </label>
                        <input type="email" required value={leadForm.email} onChange={e => setLeadForm({
                    ...leadForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-168" />
                      </div>
                      <div data-id="element-169">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-170">
                          Phone Number (Optional)
                        </label>
                        <input type="tel" value={leadForm.phone} onChange={e => setLeadForm({
                    ...leadForm,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-171" />
                      </div>
                      <button type="submit" className="w-full mt-4 bg-gold text-white py-4 uppercase tracking-wider text-sm font-semibold hover:bg-gold-dark transition-colors" data-id="element-172">
                        Request Introduction
                      </button>
                      <p className="text-xs text-center text-navy/40 mt-4" data-id="element-173">
                        By requesting an introduction, you agree to our referral
                        terms.
                      </p>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}